import { Spacing } from '@persona-id/design-tokens';
import { PlusIcon } from '@persona-id/design-tokens/icons';
import { isEqual } from 'lodash/fp';
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom-v5-compat';
import { MEDIA_WIDTHS, mediaQuery } from 'shared/styles/variables';
import styled from 'styled-components';

import DisabledProductPage from 'dashboard/components/DisabledProductPage';
import { DISABLED_CASES_PAGE } from 'dashboard/components/DisabledProductPage/config';
import FormControlLabel from 'dashboard/components/FormControls/FormControlLabel';
import InactiveEnvironmentBanner from 'dashboard/components/InactiveEnvironmentBanner';
import ModalWrapper from 'dashboard/components/ModalWrapper';
import Pagination from 'dashboard/components/Pagination';
import ReactMultiSelect from 'dashboard/components/ReactMultiSelect';
import {
  generatePlaceHolderDisplay,
  OptionType,
} from 'dashboard/components/ReactMultiSelect/ReactMultiSelect';
import SearchInput from 'dashboard/components-lib/SearchInput';
import { CloneCaseTemplateFormData } from 'dashboard/forms/FormCloneCaseTemplate';
import FormNewCaseTemplate, { NewCaseTemplateFormData } from 'dashboard/forms/FormNewCaseTemplate';
import { Container, Header, Section } from 'dashboard/layouts/IndexViewLayoutV2';
import { ModalBody, ModalContainer, ModalHeader } from 'dashboard/layouts/ModalLayout';
import { SortParams } from 'dashboard/lib/interfaces/SortParams';
import { CaseTemplate, CaseTemplateStatus } from 'dashboard/models/case-template';
import { Environment } from 'dashboard/models/environment';
import { Role } from 'dashboard/models/role';
import { CaseTemplatesFilterParams } from 'dashboard/redux/actions/case-templates';
import { initialCaseTemplatesFilterState } from 'dashboard/redux/reducers/case-templates';
import CaseTemplatesTable from 'dashboard/views/DashboardCaseTemplatesView/CaseTemplatesTable';
import Button from 'design-system/components/Button';
import Card from 'design-system/components/Card';
import { HStack } from 'design-system/components/Stack';
import usePermission from 'lib/hooks/usePermission';
import useToggle from 'lib/hooks/useToggle';
import { PaginationParams } from 'lib/interfaces/api';
import { Permission } from 'lib/permissions';

const STATUS_FILTER_OPTIONS: OptionType<CaseTemplateStatus>[] = [
  {
    label: 'Active',
    value: CaseTemplateStatus.Active,
  },
  {
    label: 'Archived',
    value: CaseTemplateStatus.Archived,
  },
];

const FilterRow = styled('div')`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr 1fr;
  grid-gap: ${Spacing[4]};

  @media ${mediaQuery({ max: MEDIA_WIDTHS.desktop })} {
    grid-template-columns: 1fr 1fr;
    grid-template-rows: auto auto;
  }
`;

const SearchContainer = styled('div')`
  grid-column: 1 / 4;

  @media ${mediaQuery({ max: MEDIA_WIDTHS.desktop })} {
    grid-column: auto;
  }
`;

const TableTitleContainer = styled('div')`
  display: grid;
  grid-template-columns: 1fr auto auto;
  grid-column-gap: ${Spacing[4]};
`;

export interface DashboardCaseTemplatesViewProps {
  currentEnvironment: Environment;
  roles: Role[];
  isLoading: boolean;
  productFeatureIsDisabled: boolean;
  hasCasesFree: boolean;
  hasCasesBasic: boolean;
  hasCasesEnhanced: boolean;
  hasCasesEnterprise: boolean;
  atActiveCaseTemplateLimit: boolean;
  caseTemplates: CaseTemplate[];
  paginationParams: PaginationParams;
  sortParams: SortParams;
  filters: CaseTemplatesFilterParams;
  pageSize: number;
  handlePageLinkClick: (pageNumber: number) => void;
  handlePageSizeClick: (pageSize: number) => void;
  handleSortUpdate: (sortParams: SortParams) => void;
  handleStatusFilterUpdate: (status: CaseTemplateStatus[]) => void;
  handleOmniSearch: (omni: string) => void;
  createCaseTemplate: (values: NewCaseTemplateFormData) => Promise<void>;
  archiveCaseTemplate: (caseTemplate: CaseTemplate) => Promise<void>;
  restoreCaseTemplate: (caseTemplate: CaseTemplate) => Promise<void>;
  cloneCaseTemplate: (token: string, formData: CloneCaseTemplateFormData) => Promise<void>;
}

const DashboardCaseTemplatesView = ({
  currentEnvironment,
  roles,
  isLoading,
  productFeatureIsDisabled,
  hasCasesFree,
  hasCasesBasic,
  hasCasesEnhanced,
  hasCasesEnterprise,
  atActiveCaseTemplateLimit,
  caseTemplates,
  paginationParams,
  sortParams,
  filters,
  pageSize,
  handlePageLinkClick,
  handlePageSizeClick,
  handleSortUpdate,
  handleStatusFilterUpdate,
  handleOmniSearch,
  createCaseTemplate,
  archiveCaseTemplate,
  restoreCaseTemplate,
  cloneCaseTemplate,
}: DashboardCaseTemplatesViewProps) => {
  const [
    showCreateCaseTemplateModal,
    { enable: openCreateCaseTemplateModal, disable: hideCreateCaseTemplateModal },
  ] = useToggle();

  const handleCreateCaseTemplateAndClose = async (values: NewCaseTemplateFormData) => {
    await createCaseTemplate(values);
    hideCreateCaseTemplateModal();
  };

  const { status, omni } = filters;

  const environmentName = currentEnvironment?.attributes?.name;

  // A filter is set by default so we check for equality rather than emptiness
  const hasUsedFilters = !isEqual(filters, initialCaseTemplatesFilterState);

  const hasCreatePermission = usePermission(Permission.CaseTemplateCreate, {
    global: true,
  });

  const location = useLocation();
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const openCreateCaseTemplateModalParam = queryParams.get('openCreateCaseTemplateModal');

    if (openCreateCaseTemplateModalParam === 'true' && hasCreatePermission) {
      openCreateCaseTemplateModal();
    }
  }, [location, hasCreatePermission]);

  if (
    productFeatureIsDisabled ||
    [hasCasesFree, hasCasesBasic, hasCasesEnhanced, hasCasesEnterprise].filter(Boolean).length === 0
  ) {
    return (
      <DisabledProductPage
        id={DISABLED_CASES_PAGE.id}
        title={DISABLED_CASES_PAGE.title}
        headerTitle={DISABLED_CASES_PAGE.headerTitle}
        description={DISABLED_CASES_PAGE.description}
        uses={DISABLED_CASES_PAGE.uses}
        image={DISABLED_CASES_PAGE.image}
      />
    );
  }

  return (
    <>
      <Container>
        <InactiveEnvironmentBanner />
        <Header title='Case templates'>
          {!isLoading && !atActiveCaseTemplateLimit && (
            <HStack>
              {hasCreatePermission && (
                <Button
                  id={`case-templates-view__create-case-template-button--${environmentName}`}
                  name='create-case-template.button'
                  variant='basic'
                  data-test={`case-templates-view__create-case-template-button--${environmentName}`}
                  onClick={openCreateCaseTemplateModal}
                  prefixIcon={<PlusIcon />}
                  aria-label='Create template'
                >
                  Create template
                </Button>
              )}
            </HStack>
          )}
        </Header>
        <Section data-test={`case-templates-view--${environmentName}`}>
          <Card>
            <Card.Body>
              <FilterRow>
                <SearchContainer>
                  <SearchInput
                    id='case-templates-view__search'
                    handleInput={handleOmniSearch}
                    value={omni || ''}
                    placeholder='Search by case template ID, key, or name'
                    dataTest='case-templates-view__search-input'
                  />
                </SearchContainer>
                <div>
                  <FormControlLabel id='case-templates-view__select-status--label'>
                    Status
                  </FormControlLabel>
                  <ReactMultiSelect
                    value={status || []}
                    options={STATUS_FILTER_OPTIONS}
                    placeholderGenerator={generatePlaceHolderDisplay({
                      label: { single: 'status', multiple: 'statuses' },
                      emptyIsAll: true,
                    })}
                    onChange={(value) => handleStatusFilterUpdate(value as CaseTemplateStatus[])}
                    selectAllEnabled
                    searchEnabled
                    controlShouldRenderValue={false}
                    hideSelectedOptions={false}
                    aria-labelledby='case-templates-view__select-status--label'
                    id='case-templates-view__select-status'
                  />
                </div>
              </FilterRow>
            </Card.Body>
          </Card>
          <Card>
            <Card.Body>
              <TableTitleContainer>
                <Card.Title>Case template list</Card.Title>
              </TableTitleContainer>
              <CaseTemplatesTable
                caseTemplates={caseTemplates}
                roles={roles}
                sortParams={sortParams}
                hasUsedFilters={hasUsedFilters}
                atActiveCaseTemplateLimit={atActiveCaseTemplateLimit}
                isLoading={isLoading}
                handleSortUpdate={handleSortUpdate}
                archiveCaseTemplate={archiveCaseTemplate}
                restoreCaseTemplate={restoreCaseTemplate}
                cloneCaseTemplate={cloneCaseTemplate}
              />
              {caseTemplates.length > 0 && (
                <Pagination
                  pageSize={pageSize}
                  paginationParams={paginationParams}
                  handlePageSizeClick={handlePageSizeClick}
                  handlePageLinkClick={handlePageLinkClick}
                />
              )}
            </Card.Body>
          </Card>
        </Section>
      </Container>
      <ModalWrapper
        isOpen={showCreateCaseTemplateModal}
        handleCloseModal={hideCreateCaseTemplateModal}
      >
        <ModalContainer>
          <ModalHeader
            data-test='case-templates-view__create-case-template-modal'
            onClose={hideCreateCaseTemplateModal}
          >
            Create a new case template
          </ModalHeader>

          <ModalBody>
            <FormNewCaseTemplate
              roles={roles}
              handleCancel={hideCreateCaseTemplateModal}
              submit={handleCreateCaseTemplateAndClose}
              defaultValues={{ 'role-ids': roles.map((role) => role.id) }}
            />
          </ModalBody>
        </ModalContainer>
      </ModalWrapper>
    </>
  );
};

export default DashboardCaseTemplatesView;
